import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail } from "lucide-react";

const TERMS_FR = [
  {
    title: "1. Objet",
    body:
      "Sereno est une plateforme web à but pédagogique destinée à aider les particuliers de 55 à 60 ans à comprendre l'épargne et l'investissement. Les présentes Conditions Générales d'Utilisation (CGU) définissent les règles d'utilisation du service.",
  },
  {
    title: "2. Acceptation",
    body:
      "En créant un compte, vous acceptez ces CGU. Si vous refusez, veuillez ne pas utiliser Sereno.",
  },
  {
    title: "3. Nature du service",
    body:
      "Sereno fournit des informations pédagogiques, des simulations et des recommandations générales. Sereno n'est PAS un conseiller en investissement financier (CIF) au sens de l'article L541-1 du Code monétaire et financier. Aucun contenu du site ne constitue un conseil personnalisé. Chaque utilisateur reste seul décideur et responsable de ses choix financiers.",
  },
  {
    title: "4. Compte utilisateur",
    body:
      "Vous vous engagez à fournir des informations exactes lors de l'inscription et à conserver votre mot de passe confidentiel. Sereno ne pourra être tenu responsable en cas d'usage frauduleux de votre compte suite à la divulgation de vos identifiants.",
  },
  {
    title: "5. Utilisation acceptable",
    body:
      "Vous vous engagez à ne pas utiliser Sereno pour des activités illégales, à ne pas tenter de compromettre la sécurité du service, à ne pas surcharger les serveurs (rate limiting en place), et à ne pas partager de contenus offensants via les fonctions de partage.",
  },
  {
    title: "6. Propriété intellectuelle",
    body:
      "Tous les contenus (textes, graphismes, logo, code) sont la propriété de Sereno. Vous disposez d'un droit d'usage personnel et non commercial.",
  },
  {
    title: "7. Limitation de responsabilité",
    body:
      "Sereno est fourni « en l'état ». Sereno ne garantit ni les performances futures d'un placement, ni l'exactitude absolue des rendements présentés (les taux affichés sont des moyennes historiques indicatives). Sereno ne saurait être tenu responsable des pertes financières résultant de décisions prises par l'utilisateur.",
  },
  {
    title: "8. Suppression du compte",
    body:
      "Vous pouvez demander la suppression de votre compte à tout moment en écrivant à bonjour@sereno.money. Sereno peut suspendre un compte en cas de violation des CGU.",
  },
  {
    title: "9. Modifications",
    body:
      "Sereno se réserve le droit de modifier ces CGU. Vous serez informé par email en cas de changement substantiel.",
  },
  {
    title: "10. Droit applicable",
    body:
      "Les présentes CGU sont soumises au droit français. Tout litige sera porté devant les tribunaux compétents du ressort du siège social de Sereno.",
  },
];

const TERMS_EN = [
  {
    title: "1. Purpose",
    body:
      "Sereno is an educational web platform designed to help individuals aged 55-60 understand savings and investment. These Terms of Use define the rules of use.",
  },
  {
    title: "2. Acceptance",
    body: "By creating an account, you accept these terms. If you refuse, please do not use Sereno.",
  },
  {
    title: "3. Nature of the service",
    body:
      "Sereno provides educational information, simulations and general recommendations. Sereno is NOT a financial investment advisor. No content on the site constitutes personalized advice. Each user remains the sole decision-maker and responsible for their financial choices.",
  },
  {
    title: "4. User account",
    body:
      "You agree to provide accurate information at registration and to keep your password confidential. Sereno cannot be held responsible for fraudulent use of your account.",
  },
  {
    title: "5. Acceptable use",
    body:
      "You agree not to use Sereno for illegal activities, not to attempt to compromise the security of the service, not to overload servers (rate limiting in place), and not to share offensive content via the sharing functions.",
  },
  {
    title: "6. Intellectual property",
    body:
      "All content (text, graphics, logo, code) is the property of Sereno. You have the right of personal and non-commercial use.",
  },
  {
    title: "7. Limitation of liability",
    body:
      "Sereno is provided \"as is\". Sereno does not guarantee future investment performance nor the absolute accuracy of returns shown (rates are indicative historical averages). Sereno cannot be held responsible for financial losses resulting from user decisions.",
  },
  {
    title: "8. Account deletion",
    body:
      "You can request deletion of your account at any time by writing to bonjour@sereno.money. Sereno may suspend an account in case of terms violation.",
  },
  {
    title: "9. Modifications",
    body:
      "Sereno reserves the right to modify these terms. You will be informed by email of any substantial change.",
  },
  {
    title: "10. Applicable law",
    body:
      "These terms are governed by French law. Any dispute will be brought before the courts having jurisdiction over Sereno's registered office.",
  },
];

const T = {
  fr: {
    back: "Retour à l'accueil",
    title: "Conditions Générales d'Utilisation",
    intro:
      "Merci d'utiliser Sereno. Ces conditions décrivent votre engagement en tant qu'utilisateur et le nôtre en tant que service.",
    terms: TERMS_FR,
  },
  en: {
    back: "Back to home",
    title: "Terms of Use",
    intro:
      "Thank you for using Sereno. These terms describe your commitment as a user and ours as a service.",
    terms: TERMS_EN,
  },
};

export default function Terms() {
  const { lang } = useLang();
  const tr = T[lang] || T.fr;
  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="terms-page">
      <Link
        to="/"
        data-testid="back-home-link"
        className="mb-6 inline-flex items-center gap-2 text-base text-[#101A42] hover:text-[#A762B1]"
      >
        <ArrowLeft className="h-4 w-4" />
        {tr.back}
      </Link>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#8E4799]">
          <FileText className="h-7 w-7" />
        </div>
        <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
          {tr.title}
        </h1>
      </div>
      <p className="text-lg leading-relaxed text-[#4A4A4A]">{tr.intro}</p>
      {tr.terms.map((s, i) => (
        <section
          key={i}
          className="mt-5 rounded-2xl border-2 border-[#E8DFC7] bg-white p-6"
          data-testid={`terms-section-${i}`}
        >
          <h2 className="font-serif text-lg font-semibold text-[#101A42]">
            {s.title}
          </h2>
          <p className="mt-2 text-base leading-relaxed text-[#101A42]">
            {s.body}
          </p>
        </section>
      ))}
    </div>
  );
}
